import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose } from 'redux';
import { omit } from 'lodash';
import { on, off, getWindowWidth } from '@sparefoot/react-core/utils/dom';

import { withClickOutside } from 'decorators';
import { GoogleMap } from 'components/core/GoogleMap';
import { SearchMarker } from 'components/search/SearchMap';
import { isLarge } from 'utils/breakpoints';
import isBrowser from 'utils/isBrowser';
import { Text } from 'components/core/Typography';
import { LOCATION_IDENTITY_KEYS } from 'utils/search/resolveSearchNavigation';

import './SearchMap.scss';

// Matches $sp-medium (731px) in app/styles/_variables.scss — the width at which
// .display-view-list-map stops hiding the map, so at/above it the map is always
// on screen regardless of the List/Map toggle.
// Deliberately NOT isLarge() (767px) or isSPMedium() (992px, despite the name —
// it actually matches SCSS $sp-large) from utils/breakpoints.js: either would
// leave the map blank between 731-766px, where CSS shows it but the gate would
// still be closed.
// Not sourced from config/breakpoints.js either: that registry's bucket names
// (via useViewportWidth's determineScreenSize) are consumed by several other
// components (NewSearchPage, Menu, NewFacilityCard, AveragePricesContent/Header).
// Adding 731 as a new key there would change their bucketing for widths in
// 731-766px too — a bigger blast radius than this one gate needs. Keep this
// literal in sync with $sp-medium by hand if that SCSS value ever changes.
const MAP_VISIBLE_MIN_WIDTH = 731;

export function SearchMap({
	className,
	facilities,
	baseUrl,
	facilitySelected,
	handleFacilityDeselected,
	handleFacilitySelected,
	handlePinSelected,
	handleSearch,
	location,
	searchType,
	pinSelected,
	handlePinDeselected,
	searchParams,
	cloudfrontUrl,
	isNewSearchVariant, // [sf16978Variation]
	onScrollToFacilityCard,
	mapShowing,
}) {
	const [searchFromMove, setSearchFromMove] = useState(false);

	// Defer loading the Google Maps API until the map is actually on screen.
	// Below MAP_VISIBLE_MIN_WIDTH the map is display:none until the user taps
	// "Map" (mapShowing), so loading it on page load is wasted work.
	// Latched: once enabled it stays enabled, so toggling back to the list view
	// hides the map via CSS without re-initialising or re-fetching anything.
	const [mapEnabled, setMapEnabled] = useState(false);
	const mapEnabledRef = useRef(false);

	useEffect(() => {
		if (mapEnabledRef.current || !isBrowser()) {
			return undefined;
		}

		const enableIfVisible = () => {
			if (mapEnabledRef.current) {
				return;
			}
			if (mapShowing || getWindowWidth() >= MAP_VISIBLE_MIN_WIDTH) {
				mapEnabledRef.current = true;
				setMapEnabled(true);
				// Unsubscribe immediately, rather than waiting for this effect's
				// own cleanup (which only runs on the next mapShowing change or
				// on unmount) — otherwise a gate opened via resize leaves this
				// listener attached for the rest of the page's life.
				off(['resize'], enableIfVisible);
			}
		};

		enableIfVisible();

		// Already on screen — no need to watch for resizes.
		if (mapEnabledRef.current) {
			return undefined;
		}

		on(['resize'], enableIfVisible);
		return () => off(['resize'], enableIfVisible);
	}, [mapShowing]);

	const handleMapMove = useCallback(
		(loc) => {
			if (loc) {
				setSearchFromMove(true);
				let locationParams = {
					...searchParams,
					location: `${loc.latitude}, ${loc.longitude}`,
				};
				// Drop the stale location-identifying params from the page/
				// search we panned away from — otherwise they ride along with
				// the new lat/lng and the backend keeps resolving the old
				// location instead of the one the map actually moved to.
				locationParams = omit(locationParams, [
					'page',
					'pageType',
					...LOCATION_IDENTITY_KEYS,
				]);
				handleSearch({ locationParams });
			}
		},
		[handleSearch, searchParams],
	);

	const variantAttr = isNewSearchVariant ? 'new' : 'old'; // [sf16978Variation]
	return (
		<div
			className={classnames('search-map', className)}
			data-variant={variantAttr}
		>
			<noscript>
				<Text
					weight="bold"
					className="noscript-message"
				>
					Please enable JavaScript to use the interactive map.
				</Text>
			</noscript>

			<GoogleMap
				center={location}
				enabled={mapEnabled}
				locationBounds={facilities.map((facility) => facility.location)}
				onMove={handleMapMove}
				radius={5}
				searchFromMove={searchFromMove}
				setSearchFromMove={setSearchFromMove}
			>
				{facilities.map((facility) => {
					const isDesktop =
						isBrowser() && isLarge(globalThis.innerWidth);
					const showMiniCard =
						facilitySelected === facility.id &&
						pinSelected &&
						(!isNewSearchVariant || !isDesktop); // suppress on desktop in new variant [sf16978Variation]
					return (
						<SearchMarker
							key={`search-marker-${facility.id}`}
							facility={facility}
							baseUrl={baseUrl}
							cloudfrontUrl={cloudfrontUrl}
							handleFacilitySelected={handleFacilitySelected}
							handleFacilityDeselected={handleFacilityDeselected}
							handlePinSelected={handlePinSelected}
							handlePinDeselected={handlePinDeselected}
							location={location}
							pinSelected={pinSelected}
							selected={facilitySelected === facility.id}
							// the mini facility card should only be shown if the pin is selected
							// but not if the facility card in the search results is the one selected
							showMiniCard={showMiniCard}
							searchType={searchType}
							searchParams={searchParams}
							isNewSearchVariant={isNewSearchVariant} // [sf16978Variation]
							onScrollToFacilityCard={onScrollToFacilityCard}
						/>
					);
				})}
			</GoogleMap>
		</div>
	);
}

SearchMap.propTypes = {
	className: PropTypes.string,
	facilities: PropTypes.arrayOf(PropTypes.object).isRequired,
	baseUrl: PropTypes.string,
	facilitySelected: PropTypes.number,
	handleFacilityDeselected: PropTypes.func,
	handleFacilitySelected: PropTypes.func,
	handlePinSelected: PropTypes.func,
	handleSearch: PropTypes.func,
	location: PropTypes.object.isRequired,
	searchType: PropTypes.string,
	pinSelected: PropTypes.bool,
	handlePinDeselected: PropTypes.func,
	searchParams: PropTypes.object,
	cloudfrontUrl: PropTypes.string,
	isNewSearchVariant: PropTypes.bool,
	onScrollToFacilityCard: PropTypes.func,
	// True once the user has switched to map view. Below MAP_VISIBLE_MIN_WIDTH
	// this is what triggers the Maps API load.
	mapShowing: PropTypes.bool,
};

export const enhance = compose((WrappedComponent) => {
	const WithClickOutsideProps = (props) => {
		const outsideClickHandler = useCallback(() => {
			props.handleFacilityDeselected();
			props.handlePinDeselected();
		}, [props.handleFacilityDeselected, props.handlePinDeselected]); // eslint-disable-line react-hooks/exhaustive-deps

		const outsideParent = props.isNewSearchVariant
			? '.search-overlay-card'
			: '.new-facility-card';

		return (
			<WrappedComponent
				{...props}
				outsideClickHandler={outsideClickHandler}
				outsideParent={outsideParent}
				outsideCondition={props.pinSelected}
			/>
		);
	};
	WithClickOutsideProps.propTypes = {
		handleFacilityDeselected: PropTypes.func,
		handlePinDeselected: PropTypes.func,
		isNewSearchVariant: PropTypes.bool,
		pinSelected: PropTypes.bool,
	};
	return WithClickOutsideProps;
}, withClickOutside);

export default enhance(SearchMap);
